/**
 * Two-column hero — title + subtitle + CTA on one side, image on
 * the other. Ported from ThemeMaker's {@code blocks/hero/HeroSplit}.
 *
 * <p>Neutral grayscale background by default. Use {@code imageSide}
 * to swap which side the image lands on. Side-by-side on desktop;
 * collapses to a single column (text above image) under 1024px.
 */
'use client';

import type { CSSProperties } from 'react';
import { tx } from '../_shared/coerce';
import { sectionInner, placeholderImg } from '../_shared/styles';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { Button } from '@akademi/ui';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface HeroSplitProps {
  title?: Localized;
  subtitle?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  image?: string;
  imageSide?: 'left' | 'right';
  /** 'full' (default) lets the columns span the section edge-to-edge;
   *  'theme' constrains them to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
}

export function HeroSplit({
  title,
  subtitle,
  ctaLabel,
  ctaHref,
  image,
  imageSide = 'right',
  maxWidth = 'full',
}: HeroSplitProps) {
  const reverse = imageSide === 'left';
  const part = useBlockPart();
  const titleText = tx(title) || 'Welcome';
  const subtitleText = tx(subtitle);
  const ctaText = tx(ctaLabel);

  // Direction trick swaps the column order without altering the JSX
  // sequence — the inner divs reset direction:ltr so text inside the
  // columns reads normally. Below the 1024px breakpoint the grid
  // drops to a single column, so the swap only matters on desktop.
  const grid: CSSProperties = {
    ...sectionInner,
    maxWidth: 'none',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 'var(--space-8, 32px)',
    alignItems: 'center',
    direction: reverse ? 'rtl' : 'ltr',
  };

  return (
    <BlockRoot
      as="section"
      style={{
        padding:
          'clamp(var(--space-10, 40px), 8vw, var(--space-20, 80px)) clamp(var(--space-4, 16px), 4vw, var(--space-10, 40px))',
        background: 'var(--color-surface-muted, #f4f4f5)',
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
    >
      <div className="hero-split-grid" style={grid}>
        <div style={{ direction: 'ltr' }}>
          <h1
            {...part('title')}
            style={{
              margin: 0,
              fontSize: 'clamp(var(--font-size-xl, 24px), 4vw, var(--font-size-2xl, 32px))',
              lineHeight: 'var(--line-height-tight, 1.15)',
              fontWeight: 'var(--font-weight-bold, 700)',
              fontFamily: 'var(--font-family-display, inherit)',
              color: 'var(--color-text-primary, #0f1218)',
            }}
          >
            {titleText}
          </h1>
          {subtitleText && (
            <p
              {...part('subtitle')}
              style={{
                marginTop: 'var(--space-4, 16px)',
                fontSize: 'clamp(var(--font-size-sm, 14px), 2vw, var(--font-size-md, 16px))',
                color: 'var(--color-text-secondary, #5f6675)',
              }}
            >
              {subtitleText}
            </p>
          )}
          {ctaText && (
            <div style={{ marginTop: 'var(--space-6, 24px)' }}>
              <Button
                {...part('cta')}
                href={ctaHref ?? '#'}
                style={{
                  height: 'auto',
                  minHeight: 44,
                  padding: 'var(--space-3, 12px) var(--space-6, 24px)',
                }}
              >
                {ctaText}
              </Button>
            </div>
          )}
        </div>
        <div style={{ direction: 'ltr' }}>
          {image ? (
            <img
              {...part('media')}
              src={resizeImageUrl(image, '1400x0')}
              alt=""
              loading="eager"
              decoding="async"
              style={{
                borderRadius: 'var(--radius-md, 8px)',
                aspectRatio: '4 / 3',
                width: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <div
              style={{
                ...placeholderImg(),
                height: 'auto',
                aspectRatio: '4 / 3',
                borderRadius: 'var(--radius-md, 8px)',
                background: 'var(--color-brand-100, #dbe6ff)',
              }}
            />
          )}
        </div>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 1024px) {
          .hero-split-grid {
            grid-template-columns: 1fr !important;
            gap: var(--space-6, 24px) !important;
          }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
